import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MiniDrawer from "../Components/MiniDrawer";
import configData from "../../config.json";
import "./HealthTracker.css";

const API_BASE = `${configData.SERVER_API_URL}/healthTracker`;

const vitalCards = [
  { id: "sugar", title: "Sugar Level", unit: "mg/dL", accent: "#F8DEBD", icon: "/Dashboard/sugarLevel.png" },
  { id: "bp", title: "Blood Pressure", unit: "mmHg", accent: "#D0FBFF", icon: "/Dashboard/bloodpressure.png" },
  { id: "temperature", title: "Temperature", unit: "Fahrenheit", accent: "#FBF0F3", icon: "/Dashboard/temperature.png" },
  { id: "weight", title: "Weight", unit: "kg", accent: "#E9F7DD", icon: "/Dashboard/weight.png" },
  { id: "urine", title: "Urine Protein", unit: "dipstick", accent: "#E8E3FF", icon: "/Pathology/urine-test.png" },
  { id: "kick", title: "Fetal Kick Counter", unit: "Cardiff count-to-ten", accent: "#FFE7D8", icon: "/babymove.png" },
  { id: "lifestyle", title: "Lifestyle Check-in", unit: "morning / evening", accent: "#E2F7EA", icon: "/Dashboard/activity.png" },
];

const historyPreview = [
  { id: "sugar", title: "Sugar", subtitle: "Fasting and post-meal readings", accent: "#F8DEBD", icon: "/Dashboard/sugarLevel.png" },
  { id: "bp", title: "Blood Pressure", subtitle: "Systolic, diastolic and pulse", accent: "#D0FBFF", icon: "/Dashboard/bloodpressure.png" },
  { id: "temperature", title: "Temperature", subtitle: "Fever and symptoms tracking", accent: "#FBF0F3", icon: "/Dashboard/temperature.png" },
  { id: "weight", title: "Weight", subtitle: "Weekly pregnancy weight trend", accent: "#E9F7DD", icon: "/Dashboard/weight.png" },
  { id: "urine", title: "Urine Protein", subtitle: "Dipstick protein readings", accent: "#E8E3FF", icon: "/Pathology/urine-test.png" },
  { id: "kick", title: "Fetal Kicks", subtitle: "Completed kick sessions", accent: "#FFE7D8", icon: "/babymove.png" },
  { id: "lifestyle", title: "Lifestyle", subtitle: "Morning and evening check-ins", accent: "#E2F7EA", icon: "/Dashboard/activity.png" },
];

const smartReferences = [
  ["PLOS ONE pregnancy health study", "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0148729"],
  ["Healthline Dehydration during pregnancy", "https://www.healthline.com/health/pregnancy/dehydration"],
  ["Mayo Clinic Dawn effect", "https://www.mayoclinic.org/diseases-conditions/diabetes/expert-answers/dawn-effect/faq-20057937"],
  ["Ubie Dawn phenomenon", "https://ubiehealth.com/doctors-note/dawn-phenomenon-high-morning-blood-sugar-causes-2744q1"],
  ["BMJ Best Practice Pregnancy topic", "https://bestpractice.bmj.com/topics/en-us/1000"],
  ["ACOG Practice Bulletin 222 PDF", "https://www.preeclampsia.org/frontend/assets/img/advocacy_resource/Gestational_Hypertension_and_Preeclampsia_ACOG_Practice_Bulletin,_Number_222_1605448006.pdf"],
  ["PMC pregnancy blood pressure study", "https://pmc.ncbi.nlm.nih.gov/articles/PMC3418980/"],
  ["NHLBI DASH eating plan", "https://www.nhlbi.nih.gov/health/dash-eating-plan"],
  ["NCBI Bookshelf pregnancy reference", "https://www.ncbi.nlm.nih.gov/books/NBK470566/"],
  ["PMC 2025 pregnancy health research", "https://pmc.ncbi.nlm.nih.gov/articles/PMC11782809/"],
  ["ObG Project Preeclampsia definitions", "https://www.obgproject.com/2017/01/08/diagnosing-preeclampsia-key-definitions/"],
  ["MSD Manual Pregnancy weight gain guidelines", "https://www.msdmanuals.com/professional/multimedia/table/guidelines-for-weight-gain-during-pregnancy"],
  ["PMC pregnancy health review", "https://pmc.ncbi.nlm.nih.gov/articles/PMC8438559/"],
  ["Diabetes Care clinical reference 2010", "https://doi.org/10.2337/dc10-0719"],
  ["Diabetes Care Standards 2019", "https://doi.org/10.2337/dc19-s014"],
  ["Diabetes Care Standards 2023", "https://doi.org/10.2337/dc23-s006"],
  ["PubMed pregnancy research article", "https://pubmed.ncbi.nlm.nih.gov/30575676/"],
  ["AHA Hypertension scientific statement", "https://www.ahajournals.org/doi/10.1161/HYP.0000000000000208"],
  ["AAFP Hypertensive disorders of pregnancy", "https://www.aafp.org/afp/2019/1115/p649"],
];

const historyActions = {
  bp: "get_bp_history",
  sugar: "get_sugar_history",
  temperature: "get_temperature_history",
  weight: "get_weight_history",
  urine: "get_urine_protein_history",
  kick: "get_kick_history",
  lifestyle: "get_lifestyle_history",
};

const initialForms = {
  bp: { systolic: "", diastolic: "", pulse: "" },
  sugar: { value: "", tag: "fasting", meal_description: "" },
  temperature: { temp_f: "", gestational_weeks: "", symptoms: "" },
  weight: { baseline_weight_kg: "", weight_kg: "" },
  urine: { reading: "negative" },
  kick: { gestational_weeks: "" },
  lifestyle: {
    check_in_type: "morning",
    sleep_rating: "",
    mood: "okay",
    symptoms: "",
    water_glasses: "",
    stress_level: "",
    diet_tag: "balanced",
  },
};

const protocolGuides = {
  sugar: {
    title: "The Protocol: How & When to Measure Sugar",
    points: [
      "Fasting: no food for 8-10 hours; water is okay. Measure after waking.",
      "Post-meal: measure exactly 2 hours from the first bite of the meal.",
      "Prefer 2-hour post-lunch or the heaviest meal for weekly checks.",
      "Wash and dry hands before testing; sugar on fingers can give false high values.",
    ],
  },
  bp: {
    title: "The Protocol: How & When to Measure BP",
    points: [
      "Measure morning 8-10 AM or evening 6-8 PM, ideally at the same time.",
      "Sit quietly for 5 minutes before measuring. Do not talk or scroll.",
      "Sit with back supported, feet flat, legs uncrossed.",
      "Keep the arm supported at heart level; place cuff on bare skin 2 cm above elbow.",
      "If BP is 140/90 or higher, rest 15 minutes and re-measure.",
    ],
  },
  temperature: {
    title: "The Protocol: How & When to Measure Temperature",
    points: [
      "Use routinely weekly, and immediately if you feel unwell.",
      "Evening 6-8 PM is useful because fevers often rise later in the day.",
      "Pregnancy can feel slightly warm; fever concern starts at 100.4 F.",
      "If warm, note symptoms like chills, burning urine, sore throat, or reduced movement.",
    ],
  },
  weight: {
    title: "The Protocol: How & When to Measure Weight",
    points: [
      "Measure once weekly, preferably morning after using the washroom.",
      "Use the same weighing scale and similar light clothing each time.",
      "Sudden gain above 2 kg/week can suggest fluid retention and needs attention.",
    ],
  },
  urine: {
    title: "The Protocol: How & When to Check Urine Protein",
    points: [
      "Log every 2 weeks, or sooner if BP is high.",
      "Use the dipstick exactly as instructed and read it at the correct time window.",
      "2+ or 3+ with high BP can indicate preeclampsia risk.",
    ],
  },
  kick: {
    title: "The Protocol: How & When to Count Kicks",
    points: [
      "Best used in the third trimester when baby movements are regular.",
      "Sit or lie on your left side and tap once for each kick, roll, or jab.",
      "Goal is 10 movements. If fewer than 10 in 2 hours, contact care urgently.",
    ],
  },
  lifestyle: {
    title: "The Protocol: How & When to Check In",
    points: [
      "Morning check-in: sleep, mood, and symptoms.",
      "Evening check-in: water intake, stress level, and diet type.",
      "Log consistently so patterns with BP and sugar can be detected.",
    ],
  },
};

function getPatientId() {
  return localStorage.getItem("userId") || "";
}

async function healthRequest(action, body = {}) {
  const response = await axios.post(`${API_BASE}/${action}`, body, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
}

function AlertBox({ color = "green", title, message }) {
  if (!message) return null;
  return (
    <div className={`ht-alert ht-alert-${color || "green"}`}>
      {title && <strong>{title}</strong>}
      <span>{message}</span>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  return moment(value).format("DD MMM YYYY, h:mm A");
}

function getHistoryValue(activeTab, item) {
  if (activeTab === "bp") return `${item.systolic}/${item.diastolic} mmHg`;
  if (activeTab === "sugar") return `${item.value} mg/dL (${item.tag})`;
  if (activeTab === "temperature") return `${item.temp_f} °F`;
  if (activeTab === "weight") return `${item.weight_kg} kg`;
  if (activeTab === "urine") return item.reading;
  if (activeTab === "kick") return `${item.kick_count} kicks in ${item.duration_mins || 0} mins`;
  if (activeTab === "lifestyle") return item.check_in_type;
  return "";
}

function HellpWizard({ script }) {
  const [nodeId, setNodeId] = useState(script?.steps?.[0]?.id);

  useEffect(() => {
    setNodeId(script?.steps?.[0]?.id);
  }, [script]);

  if (!script) return null;

  const step = script.steps?.find((item) => item.id === nodeId);
  const pathway = script.pathways?.[nodeId];

  if (pathway) {
    return (
      <div className={`ht-alert ht-alert-${pathway.color || "yellow"}`}>
        <strong>{pathway.title}</strong>
        {pathway.bot?.map((line) => <p key={line}>{line}</p>)}
        {pathway.action && <p>{pathway.action}</p>}
      </div>
    );
  }

  if (!step) return null;

  return (
    <div className="ht-alert ht-alert-yellow">
      <strong>{script.title || "Safety Check"}</strong>
      {step.bot?.map((line) => <p key={line}>{line}</p>)}
      <div className="health-action-row">
        {step.options?.map((option) => (
          <button className="ht-secondary" key={option.label} onClick={() => setNodeId(option.next)}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function HealthTracker() {
  const patientId = getPatientId();
  const [activeTab, setActiveTab] = useState("bp");
  const [forms, setForms] = useState(initialForms);
  const [history, setHistory] = useState([]);
  const [combined, setCombined] = useState(null);
  const [diet, setDiet] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [kickSession, setKickSession] = useState(null);
  const [kickGuide, setKickGuide] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [bpRemeasureRequested, setBpRemeasureRequested] = useState(false);
  const [smartLayerView, setSmartLayerView] = useState("none");
  const [historyModal, setHistoryModal] = useState(null);
  const [protocolModal, setProtocolModal] = useState(null);
  const [seenProtocols, setSeenProtocols] = useState({});

  const showProtocol = (type) => {
    if (!protocolGuides[type] || seenProtocols[type]) return;
    setProtocolModal(protocolGuides[type]);
    setSeenProtocols((prev) => ({ ...prev, [type]: true }));
  };

  const updateForm = (section, key, value) => {
    setForms((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const loadHistory = useCallback(
    async (tab = activeTab) => {
      if (!patientId || !historyActions[tab]) return;
      const body = { patient_id: Number(patientId), limit: tab === "kick" ? 14 : 30 };
      const response = await healthRequest(historyActions[tab], body);
      const records = response?.data?.history || [];
      setHistory(records);
      return records;
    },
    [activeTab, patientId]
  );

  const loadOverview = useCallback(async () => {
    if (!patientId) return;
    const [combinedRes, dietRes] = await Promise.all([
      healthRequest("get_combined_analysis", { patient_id: Number(patientId) }),
      healthRequest("get_diet_correction", { patient_id: Number(patientId) }),
    ]);
    setCombined(combinedRes?.data || null);
    setDiet(dietRes?.data?.prescriptions || []);
  }, [patientId]);

  const refreshAll = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      await Promise.all([loadHistory(activeTab), loadOverview()]);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load health tracker data.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadHistory, loadOverview]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (activeTab !== "kick" || kickGuide) return;
    healthRequest("get_kick_guide", {})
      .then((response) => setKickGuide(response?.data?.guide || null))
      .catch(() => {});
  }, [activeTab, kickGuide]);

  const submitLog = async (action, body, successMessage) => {
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const response = await healthRequest(action, body);
      if (!response.success) throw new Error(response.error || "Could not save reading.");
      setResult({ ...response.data, successMessage });
      await Promise.all([loadHistory(activeTab), loadOverview()]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Could not save reading.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (tab = activeTab) => {
    const id = Number(patientId);
    setActiveTab(tab);
    if (tab === "bp") {
      const form = forms.bp;
      const systolic = Number(form.systolic);
      const diastolic = Number(form.diastolic);
      if ((systolic >= 140 || diastolic >= 90) && !bpRemeasureRequested) {
        setBpRemeasureRequested(true);
        setResult({
          analysis: {
            color: "yellow",
            status: "Re-measure BP",
            message: "Your BP is high. Please sit quietly for 15 minutes, follow the measuring protocol, then re-measure and save again.",
          },
        });
        return;
      }
      submitLog(
        "log_bp",
        {
          patient_id: id,
          systolic,
          diastolic,
          ...(form.pulse ? { pulse: Number(form.pulse) } : {}),
        },
        "Blood pressure logged."
      );
      setBpRemeasureRequested(false);
    }
    if (tab === "sugar") {
      const form = forms.sugar;
      submitLog(
        "log_sugar",
        {
          patient_id: id,
          value: Number(form.value),
          tag: form.tag,
          ...(form.meal_description ? { meal_description: form.meal_description } : {}),
        },
        "Blood sugar logged."
      );
    }
    if (tab === "temperature") {
      const form = forms.temperature;
      submitLog(
        "log_temperature",
        {
          patient_id: id,
          temp_f: Number(form.temp_f),
          ...(form.gestational_weeks ? { gestational_weeks: Number(form.gestational_weeks) } : {}),
          ...(form.symptoms ? { symptoms: form.symptoms } : {}),
        },
        "Temperature logged."
      );
    }
    if (tab === "weight") {
      submitLog("log_weight", { patient_id: id, weight_kg: Number(forms.weight.weight_kg) }, "Weight logged.");
    }
    if (tab === "urine") {
      submitLog("log_urine_protein", { patient_id: id, reading: forms.urine.reading }, "Urine protein logged.");
    }
    if (tab === "lifestyle") {
      const form = forms.lifestyle;
      submitLog(
        "log_lifestyle",
        {
          patient_id: id,
          check_in_type: form.check_in_type,
          ...(form.sleep_rating ? { sleep_rating: Number(form.sleep_rating) } : {}),
          ...(form.mood ? { mood: form.mood } : {}),
          ...(form.symptoms ? { symptoms: form.symptoms } : {}),
          ...(form.water_glasses ? { water_glasses: Number(form.water_glasses) } : {}),
          ...(form.stress_level ? { stress_level: Number(form.stress_level) } : {}),
          ...(form.diet_tag ? { diet_tag: form.diet_tag } : {}),
        },
        "Lifestyle check-in saved."
      );
    }
  };

  const setBaselineWeight = () => {
    submitLog(
      "set_baseline_weight",
      { patient_id: Number(patientId), weight_kg: Number(forms.weight.baseline_weight_kg) },
      "Baseline weight saved."
    );
  };

  const startKickSession = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await healthRequest("start_kick_session", {
        patient_id: Number(patientId),
        ...(forms.kick.gestational_weeks ? { gestational_weeks: Number(forms.kick.gestational_weeks) } : {}),
      });
      setKickSession({
        session_id: response.data.session_id,
        kick_count: 0,
        elapsed_mins: 0,
        message: response.data.message,
      });
      setKickGuide(response.data.guide || kickGuide);
      setResult({ successMessage: response.data.message });
    } catch (err) {
      setError(err.response?.data?.error || "Could not start kick session.");
    } finally {
      setLoading(false);
    }
  };

  const logKick = async () => {
    if (!kickSession?.session_id) return;
    setLoading(true);
    try {
      const response = await healthRequest("log_kick", {
        patient_id: Number(patientId),
        session_id: kickSession.session_id,
      });
      setKickSession((prev) => ({ ...prev, ...response.data }));
      setResult({ successMessage: response.data.nudge || (response.data.goal_reached ? "Goal reached." : "Kick saved.") });
    } catch (err) {
      setError(err.response?.data?.error || "Could not log kick.");
    } finally {
      setLoading(false);
    }
  };

  const endKickSession = async () => {
    if (!kickSession?.session_id) return;
    await submitLog(
      "end_kick_session",
      { patient_id: Number(patientId), session_id: kickSession.session_id },
      "Kick session ended."
    );
    setKickSession(null);
  };

  return (
    <div className="health-tracker-page">
      <div className="health-tracker-layout">
        <MiniDrawer />
        <main className="health-tracker-main">
          <header className="health-tracker-header">
            <div>
              <div className="health-title-row">
                <span className="health-title-icon">
                  <img src="/Dashboard/healthTrackerDark.png" alt="" />
                </span>
                <h1 className="health-tracker-title">Health Tracker</h1>
              </div>
              <p className="health-tracker-subtitle">
                Track sugar, blood pressure, temperature, weight, urine protein, fetal kicks, and daily lifestyle check-ins.
              </p>
            </div>
            <button className="health-tracker-refresh" onClick={refreshAll} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </header>

          <section className="health-intro-card">
            <div>
              <h2>Health Vitals</h2>
              <p>Enter your readings from the health kit. Your care rules will show guidance instantly.</p>
            </div>
            <label className="health-date-picker">
              <span>Date</span>
              <input type="date" value={selectedDate} max={moment().format("YYYY-MM-DD")} onChange={(event) => setSelectedDate(event.target.value)} />
            </label>
          </section>

          <div className="health-tracker-alerts">
            {error && <AlertBox color="red" title="Error" message={error} />}
          </div>

          <div className="health-desktop-grid">
            <section className="health-vitals-column">
              <SectionTitle title="Health Vitals" subtitle="Enter your readings from the health kit." />
              <div className="app-card-stack">
                {vitalCards.map((card) => (
                  <VitalCard key={card.id} card={card}>
                    <VitalFields
                      type={card.id}
                      forms={forms}
                      updateForm={updateForm}
                      kickSession={kickSession}
                      loading={loading}
                      setBaselineWeight={setBaselineWeight}
                      startKickSession={startKickSession}
                      logKick={logKick}
                      endKickSession={endKickSession}
                      onProtocolRequest={() => showProtocol(card.id)}
                      handleSubmit={() => {
                        handleSubmit(card.id);
                      }}
                    />
                    {result?.successMessage && activeTab === card.id && <AlertBox color="green" title="Saved" message={result.successMessage} />}
                    {result?.analysis && activeTab === card.id && <AlertBox color={result.analysis.color} title={result.analysis.status} message={result.analysis.message} />}
                    {result?.combined_alert && activeTab === card.id && <AlertBox color="critical" title="Combined Alert" message={result.combined_alert} />}
                    {result?.delta_3015_alert?.triggered && activeTab === card.id && <AlertBox color={result.delta_3015_alert.color} title="BP Trend" message={result.delta_3015_alert.message} />}
                    {result?.rise_trend_alert?.triggered && activeTab === card.id && <AlertBox color={result.rise_trend_alert.color} title="BP Rise Trend" message={result.rise_trend_alert.message} />}
                    {result?.alerts?.map((alert) => activeTab === card.id && <AlertBox key={alert.message} color={alert.color} title={alert.type} message={alert.message} />)}
                    {card.id === "bp" && <HellpWizard script={result?.hellp_script} />}
                    {card.id === "kick" && kickGuide && <KickGuide guide={kickGuide} />}
                  </VitalCard>
                ))}

                <VitalCard card={{ title: "Smart Analysis & Solution", unit: "analysis and solution", accent: "#E0F2FF", icon: "/Dashboard/healthtracker.png" }}>
                  <div className="health-action-row">
                    <button
                      className="ht-secondary"
                      disabled={loading}
                      onClick={() => {
                        setSmartLayerView("analysis");
                        refreshAll();
                      }}
                    >
                      Analysis
                    </button>
                    <button
                      className="ht-secondary"
                      disabled={loading}
                      onClick={() => {
                        setSmartLayerView("diet");
                        refreshAll();
                      }}
                    >
                      Solution
                    </button>
                  </div>
                  {smartLayerView === "analysis" && combined?.alerts?.slice(0, 3).map((alert) => (
                    <AlertBox key={`${alert.scenario}-${alert.message}`} color={alert.color} title={alert.scenario?.replaceAll("_", " ")} message={alert.message} />
                  ))}
                  {smartLayerView === "diet" && diet.slice(0, 3).map((item) => (
                    <div className="guide-step" key={`${item.trigger}-${item.prescription}`}>
                      <strong>{item.trigger}</strong>
                      <p>{item.prescription}</p>
                    </div>
                  ))}
                  <SmartReferences />
                </VitalCard>
              </div>
            </section>

            <aside className="health-history-column">
              <SectionTitle title="Recent History" subtitle="A quick view style matching the app dashboard." />
              <div className="history-preview-list">
                {historyPreview.map((item) => (
                  <button
                    key={item.id}
                    className="history-preview-item"
                    onClick={async () => {
                      setActiveTab(item.id);
                      setHistoryModal(item);
                      await loadHistory(item.id);
                    }}
                  >
                    <span className="history-preview-icon" style={{ background: item.accent }}>
                      <img src={item.icon} alt="" />
                    </span>
                    <span>
                      <b>{item.title}</b>
                      <em>Tap to view graph</em>
                    </span>
                    <strong>›</strong>
                  </button>
                ))}
              </div>
            </aside>
          </div>

          {historyModal && (
            <HistoryModal
              item={historyModal}
              activeTab={activeTab}
              history={history}
              onClose={() => setHistoryModal(null)}
            />
          )}

          {protocolModal && (
            <ProtocolModal
              guide={protocolModal}
              onClose={() => setProtocolModal(null)}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function SmartReferences() {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showAllReferences, setShowAllReferences] = useState(false);
  const visibleReferences = showAllReferences ? smartReferences : smartReferences.slice(0, 5);
  const hiddenReferenceCount = smartReferences.length - 5;

  return (
    <div className="smart-reference-card">
      <h3>Smart Analysis References</h3>
      <p>Clinical and public-health references used for AI analysis and solution guidance.</p>
      <div className="smart-reference-links">
        {visibleReferences.map(([label, href]) => (
          <a href={href} key={label} target="_blank" rel="noreferrer">🔗 {label}</a>
        ))}
      </div>
      <button type="button" onClick={() => setShowAllReferences((prev) => !prev)}>
        {showAllReferences ? "Show less" : `Read more (${hiddenReferenceCount} more)`}
      </button>
      <div className="smart-disclaimer">
        <strong>Disclaimer</strong>
        {showDisclaimer ? (
          <span>
            Myprega is a daily support companion. Our services, including Health Tracking and Analysis, and Nurture AI report analysis, provide clarity, awareness, and guidance, not a medical diagnosis or medicine prescription. It is not a medical emergency tool. Please always consult your gynecologist or doctors for medical treatments, emergencies and decisions. Myprega is not designed to provide emergency healthcare services, emergency triage, emergency monitoring, or emergency medical response.{" "}
            <button type="button" className="smart-inline-button" onClick={() => setShowDisclaimer(false)}>
              Show less
            </button>
          </span>
        ) : (
          <span>
            Myprega is a daily support...{" "}
            <button type="button" className="smart-inline-button" onClick={() => setShowDisclaimer(true)}>
              Read more
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

function ProtocolModal({ guide, onClose }) {
  return (
    <div className="protocol-modal-backdrop">
      <div className="protocol-modal" role="dialog" aria-modal="true">
        <h2>{guide.title}</h2>
        <ul>
          {guide.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <button type="button" onClick={onClose}>
          Continue
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onFocus }) {
  return (
    <div className="health-field">
      <label>{label}</label>
      <input type="number" value={value} onFocus={onFocus} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, onFocus }) {
  return (
    <div className="health-field">
      <label>{label}</label>
      <select value={value} onFocus={onFocus} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replace("_", " ")}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, onFocus }) {
  return (
    <div className="health-field full">
      <label>{label}</label>
      <textarea value={value} onFocus={onFocus} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="app-section-title">
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
  );
}

function VitalCard({ card, children }) {
  return (
    <section className="app-vital-card">
      <div className="app-vital-head">
        <span className="app-vital-icon" style={{ background: card.accent }}>
          <img src={card.icon} alt="" />
        </span>
        <span>
          <h3>{card.title}</h3>
          <p>{card.unit}</p>
        </span>
      </div>
      {children}
    </section>
  );
}

function VitalFields({
  type,
  forms,
  updateForm,
  kickSession,
  loading,
  setBaselineWeight,
  startKickSession,
  logKick,
  endKickSession,
  onProtocolRequest,
  handleSubmit,
}) {
  if (type === "sugar") {
    return (
      <>
        <SegmentedChoice values={{ fasting: "Fasting", post_meal: "Post Meal" }} selected={forms.sugar.tag} onChange={(value) => updateForm("sugar", "tag", value)} />
        <div className="health-form-grid health-form-grid--one">
          <Field label="Reading" value={forms.sugar.value} onFocus={onProtocolRequest} onChange={(value) => updateForm("sugar", "value", value)} />
          {forms.sugar.tag === "post_meal" && <TextField label="Meal description" value={forms.sugar.meal_description} onFocus={onProtocolRequest} onChange={(value) => updateForm("sugar", "meal_description", value)} />}
        </div>
        <SaveButton loading={loading} onClick={handleSubmit} />
      </>
    );
  }

  if (type === "bp") {
    return (
      <>
        <div className="health-form-grid">
          <Field label="Systolic" value={forms.bp.systolic} onFocus={onProtocolRequest} onChange={(value) => updateForm("bp", "systolic", value)} />
          <Field label="Diastolic" value={forms.bp.diastolic} onFocus={onProtocolRequest} onChange={(value) => updateForm("bp", "diastolic", value)} />
          <Field label="Pulse" value={forms.bp.pulse} onFocus={onProtocolRequest} onChange={(value) => updateForm("bp", "pulse", value)} />
        </div>
        <SaveButton loading={loading} onClick={handleSubmit} />
      </>
    );
  }

  if (type === "temperature") {
    return (
      <>
        <div className="health-form-grid health-form-grid--one">
          <Field label="Temperature" value={forms.temperature.temp_f} onFocus={onProtocolRequest} onChange={(value) => updateForm("temperature", "temp_f", value)} />
          <TextField label="Symptoms" value={forms.temperature.symptoms} onFocus={onProtocolRequest} onChange={(value) => updateForm("temperature", "symptoms", value)} />
        </div>
        <SaveButton loading={loading} onClick={handleSubmit} />
      </>
    );
  }

  if (type === "weight") {
    return (
      <>
        <div className="health-form-grid health-form-grid--one">
          <Field label="Weight" value={forms.weight.weight_kg} onFocus={onProtocolRequest} onChange={(value) => updateForm("weight", "weight_kg", value)} />
          <Field label="Baseline weight" value={forms.weight.baseline_weight_kg} onFocus={onProtocolRequest} onChange={(value) => updateForm("weight", "baseline_weight_kg", value)} />
        </div>
        <div className="health-action-row split">
          <button className="ht-secondary" onClick={setBaselineWeight} disabled={loading || !forms.weight.baseline_weight_kg}>Set Baseline Once</button>
          <button className="ht-primary" onClick={handleSubmit} disabled={loading || !forms.weight.weight_kg}>Log Weight</button>
        </div>
      </>
    );
  }

  if (type === "urine") {
    return (
      <>
        <SelectField label="Reading" value={forms.urine.reading} onFocus={onProtocolRequest} onChange={(value) => updateForm("urine", "reading", value)} options={["negative", "trace", "1+", "2+", "3+"]} />
        <SaveButton loading={loading} onClick={handleSubmit} />
      </>
    );
  }

  if (type === "kick") {
    return (
      <>
        <Field label="Gestational weeks" value={forms.kick.gestational_weeks} onFocus={onProtocolRequest} onChange={(value) => updateForm("kick", "gestational_weeks", value)} />
        <div className="app-kick-box">
          {kickSession ? `Active session: ${kickSession.kick_count || 0} kicks logged` : "Start a session, then tap once for every kick/roll/jab."}
        </div>
        <div className="health-action-row thirds">
          <button className="ht-secondary" onClick={startKickSession} disabled={loading}>{kickSession ? "Restart" : "Start"}</button>
          <button className="ht-primary" onClick={logKick} disabled={loading || !kickSession}>Kick</button>
          <button className="ht-secondary" onClick={endKickSession} disabled={loading || !kickSession}>End</button>
        </div>
      </>
    );
  }

  if (type === "lifestyle") {
    const isMorning = forms.lifestyle.check_in_type === "morning";
    return (
      <>
        <SegmentedChoice values={{ morning: "Morning", evening: "Evening" }} selected={forms.lifestyle.check_in_type} onChange={(value) => updateForm("lifestyle", "check_in_type", value)} />
        {isMorning ? (
          <div className="health-form-grid">
            <SliderField label="Sleep rating" value={forms.lifestyle.sleep_rating || 4} min={1} max={5} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "sleep_rating", value)} />
            <SelectField label="Mood" value={forms.lifestyle.mood} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "mood", value)} options={["energetic", "okay", "exhausted", "anxious"]} />
            <TextField label="Symptoms" value={forms.lifestyle.symptoms} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "symptoms", value)} />
          </div>
        ) : (
          <div className="health-form-grid">
            <SliderField label="Water glasses" value={forms.lifestyle.water_glasses || 6} min={0} max={8} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "water_glasses", value)} />
            <SliderField label="Stress level" value={forms.lifestyle.stress_level || 3} min={1} max={5} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "stress_level", value)} />
            <SelectField label="Diet" value={forms.lifestyle.diet_tag} onFocus={onProtocolRequest} onChange={(value) => updateForm("lifestyle", "diet_tag", value)} options={["balanced", "carb_heavy", "junk", "skipped_meal"]} />
          </div>
        )}
        <SaveButton loading={loading} onClick={handleSubmit} />
      </>
    );
  }

  return null;
}

function SegmentedChoice({ values, selected, onChange }) {
  return (
    <div className="segmented-choice">
      {Object.entries(values).map(([value, label]) => (
        <button className={selected === value ? "selected" : ""} key={value} onClick={() => onChange(value)} type="button">
          {label}
        </button>
      ))}
    </div>
  );
}

function SliderField({ label, value, min, max, onChange, onFocus }) {
  return (
    <div className="slider-field health-field full">
      <div className="slider-label">
        <label>{label}</label>
        <strong>{value}</strong>
      </div>
      <input type="range" min={min} max={max} value={value} onFocus={onFocus} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function SaveButton({ loading, onClick, label = "Save Reading" }) {
  return (
    <button className="ht-primary app-save-button" onClick={onClick} disabled={loading}>
      {loading ? "Saving..." : label}
    </button>
  );
}

function KickGuide({ guide }) {
  return (
    <div className="guide-list">
      {guide.steps?.map((step) => (
        <div className="guide-step" key={step.step}>
          <strong>{step.title}</strong>
          <p>{step.detail || step.count}</p>
          {step.tip && <p>{step.tip}</p>}
          {step.do_not_count && <p>Do not count: {step.do_not_count}</p>}
        </div>
      ))}
    </div>
  );
}

function HistoryModal({ item, activeTab, history, onClose }) {
  const chart = buildChart(activeTab, history);

  return (
    <div className="history-modal-backdrop" onClick={onClose}>
      <section className="history-modal" onClick={(event) => event.stopPropagation()}>
        <div className="history-modal-handle" />
        <header className="history-modal-header">
          <span className="history-modal-icon" style={{ background: item.accent }}>
            <img src="/Dashboard/activity.png" alt="" />
          </span>
          <h2>{item.title} History</h2>
          <button type="button" onClick={onClose}>×</button>
        </header>

        <div className="history-modal-body">
          {chart.data.length > 0 && (
            <div className="history-chart-card">
              <div className="history-chart-head">
                <h3>{item.title} Trend</h3>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chart.data} margin={{ top: 12, right: 18, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#f3dfe8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#7a7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#7a7280", fontSize: 12 }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip />
                  <Legend iconType="circle" />
                  {chart.lines.map((line) => (
                    <Line
                      key={line.key}
                      type="monotone"
                      dataKey={line.key}
                      name={line.name}
                      stroke={line.color}
                      strokeWidth={3}
                      dot={{ r: 5, fill: line.color }}
                      activeDot={{ r: 7 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {history.length === 0 ? (
            <div className="history-modal-empty">No history found yet.</div>
          ) : (
            history.map((record) => (
              <HistoryRecord key={`${activeTab}-${record.id}-${record.recorded_at || record.session_start}`} activeTab={activeTab} record={record} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function HistoryRecord({ activeTab, record }) {
  const color = record.color || "green";
  const message = record.message || "";
  const chips = historyChips(record);

  return (
    <article className="history-record-card">
      <div className={`history-status-dot history-status-${color}`} />
      <div className="history-record-content">
        <h3>{getHistoryValue(activeTab, record)}</h3>
        {record.status && <strong className={`history-record-status history-status-text-${color}`}>{String(record.status).replaceAll("_", " ")}</strong>}
        {message && <p className={`history-record-message history-record-message-${color}`}>{message}</p>}
        {chips.length > 0 && (
          <div className="history-chip-row">
            {chips.map((chip) => <span key={chip}>{chip}</span>)}
          </div>
        )}
        <time>{formatDate(record.recorded_at || record.session_start || record.created_at)}</time>
      </div>
    </article>
  );
}

function historyChips(record) {
  const chips = [];
  const add = (label, value) => {
    const text = `${value ?? ""}`.trim();
    if (text && text !== "null") chips.push(`${label}: ${text.replaceAll("_", " ")}`);
  };
  add("Type", record.tag);
  add("Meal", record.meal_description);
  add("Pulse", record.pulse ? `${record.pulse} bpm` : "");
  add("Symptoms", record.symptoms);
  add("Week", record.gestational_weeks);
  add("Gain/week", record.gain_kg_per_week ? `${record.gain_kg_per_week} kg` : "");
  add("Reading", record.reading);
  add("Kicks", record.kick_count);
  add("Duration", record.duration_mins ? `${record.duration_mins} min` : "");
  add("Check-in", record.check_in_type);
  add("Sleep", record.sleep_rating);
  add("Mood", record.mood);
  add("Water", record.water_glasses ? `${record.water_glasses} glasses` : "");
  add("Stress", record.stress_level);
  add("Diet", record.diet_tag);
  return chips;
}

function buildChart(activeTab, records) {
  const ordered = [...records].reverse();
  const line = (key, name, color) => ({ key, name, color });

  if (activeTab === "sugar") {
    return {
      lines: [line("fasting", "Fasting", "#f0588b"), line("postMeal", "Post meal", "#1495a3")],
      data: ordered.map((item) => ({
        date: moment(item.recorded_at).format("DD-MM-YYYY"),
        fasting: item.tag === "fasting" ? Number(item.value) : null,
        postMeal: item.tag === "post_meal" ? Number(item.value) : null,
      })),
    };
  }

  if (activeTab === "bp") {
    return {
      lines: [line("systolic", "Sys", "#f0588b"), line("diastolic", "Dia", "#1495a3")],
      data: ordered.map((item) => ({
        date: moment(item.recorded_at).format("DD-MM-YYYY"),
        systolic: Number(item.systolic),
        diastolic: Number(item.diastolic),
      })),
    };
  }

  const keyMap = {
    temperature: ["value", "Temp", "temp_f"],
    weight: ["value", "Weight", "weight_kg"],
    urine: ["value", "Protein", "reading"],
    kick: ["value", "Mins", "duration_mins"],
    lifestyle: ["value", "Stress", "stress_level"],
  };
  const [key, name, source] = keyMap[activeTab] || ["value", "Value", "value"];
  const urineScale = { negative: 0, trace: 1, "1+": 2, "2+": 3, "3+": 4 };

  return {
    lines: [line(key, name, "#f0588b")],
    data: ordered.map((item) => ({
      date: moment(item.recorded_at || item.session_start).format("DD-MM-YYYY"),
      [key]: activeTab === "urine" ? urineScale[item[source]] : Number(item[source]),
    })).filter((item) => Number.isFinite(item[key])),
  };
}
